import {
  getAdminPermissionsForRoles,
  type AdminPermission,
  type AdminRole,
} from "../../contracts/admin-permissions";

export type AdminFixtureSession = {
  roles?: AdminRole[];
};

export type AdminAuthSession = {
  authMode: "fixture" | "admin-auth";
  roles: AdminRole[];
  getAccessToken: () => Promise<string | undefined> | string | undefined;
  source: "runtime-config" | "fixture-session" | "admin-auth-provider";
};

export type AdminAuthSessionConfig = {
  authMode: "fixture" | "admin-auth";
  runtimeRoles: AdminRole[];
  fixtureSession?: AdminFixtureSession;
  getAccessToken?: () => Promise<string | undefined> | string | undefined;
};

export type AdminPermissionResult =
  | { ok: true; permission: AdminPermission }
  | {
      ok: false;
      permission: AdminPermission;
      error: {
        code: "ADMIN_QUERY_PERMISSION_DENIED";
        message: string;
        requestId: string;
      };
    };

export class AdminAuthSessionBootstrapError extends Error {
  readonly code = "ADMIN_AUTH_BOOTSTRAP_INVALID";

  constructor(message: string) {
    super(message);
    this.name = "AdminAuthSessionBootstrapError";
  }
}

function isAccessTokenProvider(
  value: AdminAuthSessionConfig["getAccessToken"],
): value is NonNullable<AdminAuthSessionConfig["getAccessToken"]> {
  return typeof value === "function";
}

function resolveFixtureRoles(config: AdminAuthSessionConfig): {
  roles: AdminRole[];
  source: AdminAuthSession["source"];
} {
  if (config.fixtureSession?.roles && config.fixtureSession.roles.length > 0) {
    return {
      roles: config.fixtureSession.roles,
      source: "fixture-session",
    };
  }

  return {
    roles: config.runtimeRoles,
    source: "runtime-config",
  };
}

export function validateAdminAuthSessionConfig(config: AdminAuthSessionConfig): AdminAuthSessionBootstrapError | null {
  if (config.authMode !== "admin-auth") {
    return null;
  }

  if (!isAccessTokenProvider(config.getAccessToken)) {
    return new AdminAuthSessionBootstrapError(
      "Admin auth bootstrap requires an in-memory getAccessToken provider.",
    );
  }

  return null;
}

export function createAdminAuthSession(config: AdminAuthSessionConfig): AdminAuthSession {
  if (config.authMode === "fixture") {
    const resolved = resolveFixtureRoles(config);

    return {
      authMode: "fixture",
      roles: resolved.roles,
      source: resolved.source,
      getAccessToken: () => undefined,
    };
  }

  const validationError = validateAdminAuthSessionConfig(config);
  if (validationError) {
    throw validationError;
  }

  const getAccessToken = config.getAccessToken;
  if (!isAccessTokenProvider(getAccessToken)) {
    throw new AdminAuthSessionBootstrapError(
      "Admin auth bootstrap requires an in-memory getAccessToken provider.",
    );
  }

  return {
    authMode: "admin-auth",
    roles: config.runtimeRoles,
    source: "admin-auth-provider",
    getAccessToken,
  };
}

export function requireAdminPermission(
  session: AdminAuthSession,
  permission: AdminPermission,
): AdminPermissionResult {
  const permissions = getAdminPermissionsForRoles(session.roles);

  if (permissions.includes(permission)) {
    return {
      ok: true,
      permission,
    };
  }

  return {
    ok: false,
    permission,
    error: {
      code: "ADMIN_QUERY_PERMISSION_DENIED",
      message: `Admin permission denied for ${permission}.`,
      requestId: `admin_permission_denied_${permission.replaceAll(".", "_")}`,
    },
  };
}

// UI hiding is only a convenience boundary. The Admin Query API must validate the same permission again.
